import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenLine } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/supabase";

import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const plan = params.get("plan");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedLgpd, setAcceptedLgpd] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres");
    if (!acceptedLgpd) return toast.error("Você precisa aceitar os termos de uso e a LGPD");
    
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Conta criada! Vamos começar.");
      if (plan) {
        nav(`/app/checkout?plan=${plan}`);
      } else {
        nav("/app/dashboard");
      }

    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
          <span className="font-serif text-2xl font-bold">RevisaPro</span>
        </Link>
        <h1 className="font-serif text-4xl mb-2">Criar conta</h1>
        <p className="text-[#4A4A4A] mb-8">Leva menos de 1 minuto.</p>
        <form onSubmit={onSubmit} className="space-y-5 bg-white border border-black/10 p-8">
          <div>
            <Label htmlFor="name" className="text-xs font-mono uppercase tracking-widest mb-2 block">Nome completo</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
              className="rounded-none border-black/20" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest mb-2 block">E-mail</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="rounded-none border-black/20" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest mb-2 block">Senha</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="rounded-none border-black/20" data-testid="register-password-input" />
          </div>

          <div className="flex items-start space-x-3 pt-2">
            <Checkbox 
              id="lgpd" 
              checked={acceptedLgpd} 
              onCheckedChange={setAcceptedLgpd}
              className="mt-0.5 rounded-none"
            />
            <Label 
              htmlFor="lgpd" 
              className="text-[13px] text-[#4A4A4A] leading-tight cursor-pointer font-normal"
            >
              Eu autorizo o tratamento dos meus dados pessoais de acordo com a LGPD e concordo com os Termos de Uso.
            </Label>
          </div>

          <Button type="submit" disabled={loading}
            className="w-full rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white py-6"
            data-testid="register-submit-btn">
            {loading ? "Criando..." : "Criar conta grátis"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-[#4A4A4A] text-center">
          Já tem conta?{" "}
          <Link
            to={plan ? `/login?plan=${plan}` : "/login"}
            className="text-[#D9381E] underline underline-offset-2"
          >
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
